import { useState, type FormEvent } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/dahfnebp14a1h3mr6l10';

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function ContactForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const guard = String(formData.get('website_alt') || '').trim();
    if (guard) {
      setStatus('success');
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      if (typeof value === 'string') payload.append(key, value);
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();

      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = String(serverMsg).toLowerCase().includes('spam');
      const okCode = parsed?.code === 'OK';

      if (response.ok && okCode && !isSpam) {
        setStatus('success');
        formEl.reset();
      } else {
        setStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请检查网络后重试。');
    }
  };

  if (status === 'success') {
    return (
      <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-8 text-center">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-secondary-500 text-background-50">
          <i className="ri-check-double-line text-2xl"></i>
        </span>
        <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">已收到，感谢你</h3>
        <p className="mt-2 text-sm text-foreground-600">
          我们会在核对素材后更新对应档案。如需回复，会通过你留下的邮箱联系。
        </p>
        <button
          type="button"
          onClick={() => setStatus('idle')}
          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-secondary-300 px-5 py-2.5 text-sm text-secondary-800 transition-colors hover:bg-secondary-100"
        >
          再提交一条
        </button>
      </div>
    );
  }

  return (
    <form
      data-readdy-form
      id="dahfnebp14a1h3mr6l10"
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8"
    >
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="name" className="mb-2 block text-sm font-medium text-foreground-800">
            你的称呼
          </label>
          <input
            id="name"
            name="name"
            type="text"
            required
            maxLength={40}
            placeholder="怎么称呼你"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
        <div>
          <label htmlFor="email" className="mb-2 block text-sm font-medium text-foreground-800">
            联系邮箱
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            maxLength={80}
            placeholder="you@example.com"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
        <div>
          <label htmlFor="topic" className="mb-2 block text-sm font-medium text-foreground-800">
            反馈类型
          </label>
          <select
            id="topic"
            name="topic"
            defaultValue="素材投稿"
            className="w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400"
          >
            <option value="素材投稿">素材投稿</option>
            <option value="纠错指正">纠错指正</option>
            <option value="补充进展">补充进展</option>
            <option value="合作与建议">合作与建议</option>
          </select>
        </div>
        <div>
          <label htmlFor="article_link" className="mb-2 block text-sm font-medium text-foreground-800">
            相关档案链接（选填）
          </label>
          <input
            id="article_link"
            name="article_link"
            type="text"
            maxLength={120}
            placeholder="粘贴对应档案的链接或标题"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
      </div>

      <div className="mt-5">
        <label htmlFor="message" className="mb-2 block text-sm font-medium text-foreground-800">
          想说的内容
        </label>
        <textarea
          id="message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="请尽量提供可公开核查的出处与时间点，最多 500 字"
          className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
        ></textarea>
      </div>

      <div className="form-guard-field" aria-hidden="true">
        <label htmlFor="website_alt">备用联系方式</label>
        <input
          id="website_alt"
          name="website_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
        />
      </div>

      {status === 'error' && formError && (
        <p className="mt-4 rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-700">
          {formError}
        </p>
      )}

      <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs leading-relaxed text-foreground-500">
          我们只接受公开可查的素材，不接收任何涉及个人隐私的内容。
        </p>
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status === 'submitting' ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              提交中
            </>
          ) : (
            <>
              <i className="ri-send-plane-line"></i>
              提交反馈
            </>
          )}
        </button>
      </div>
    </form>
  );
}